import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios, API, useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, Save, Loader2, Plus, Trash2, GitBranch, GripVertical, Terminal, Users, RefreshCw, Download, Image, Upload, AlertTriangle, Paperclip, Search, ShieldAlert, Copy, Check, Sparkles } from "lucide-react";
import TemplatesDialog from "@/components/TemplatesDialog";

export default function BotStudioPage() {
  const { permissions } = useAuth();
  const canExport = !permissions || permissions.can_access_group_exports !== false;
  const canPrivateBot = !permissions || permissions.can_access_private_bot !== false;
  const canAI = !permissions || permissions.can_access_ai_replies !== false;
  const canFlows = !permissions || permissions.can_access_flows !== false;
  const [botSettings, setBotSettings] = useState({ enabled: false, system_prompt: "", fallback_reply: "", any_message_trigger: false, trigger_keyword: "", default_flow_id: null, ai_enabled: false, ai_only_mode: false, ai_system_prompt: "", ai_history_limit: 8, ai_in_private: true, ai_in_groups: false, ai_model: "claude-sonnet-4-5-20250929" });
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [groupSettings, setGroupSettings] = useState({ enabled: false, trigger_mode: "mention_only", trigger_keyword: "", trigger_command: "/bot", group_command: "" });
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savedGeneral, setSavedGeneral] = useState(false);
  const [flows, setFlows] = useState([]);
  const [editingFlow, setEditingFlow] = useState(null);
  const [savingFlow, setSavingFlow] = useState(false);
  const [commands, setCommands] = useState([]);
  const [editingCmd, setEditingCmd] = useState(null);
  const [savingCmd, setSavingCmd] = useState(false);
  const [groups, setGroups] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportWarning, setExportWarning] = useState(null);
  const [adminInstructions, setAdminInstructions] = useState(null);
  const [adminInstructionsLoading, setAdminInstructionsLoading] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [exportingSingle, setExportingSingle] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [media, setMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingStepMedia, setUploadingStepMedia] = useState(null);
  const [uploadingCmdMedia, setUploadingCmdMedia] = useState(false);
  const [dragOverMedia, setDragOverMedia] = useState(false);
  const [uploadMediaName, setUploadMediaName] = useState("");
  const [loading, setLoading] = useState(true);
  const mediaFileRef = useRef(null);
  const stepFileRefs = useRef({});
  const cmdFileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [botRes, groupRes, flowsRes, cmdsRes, groupsRes, mediaRes] = await Promise.all([
        authAxios.get(`${API}/settings/bot`).catch(() => ({ data: {} })),
        authAxios.get(`${API}/settings/group`).catch(() => ({ data: {} })),
        authAxios.get(`${API}/flows`).catch(() => ({ data: [] })),
        authAxios.get(`${API}/group-commands`).catch(() => ({ data: [] })),
        authAxios.get(`${API}/groups`).catch(() => ({ data: [] })),
        authAxios.get(`${API}/media`).catch(() => ({ data: [] })),
      ]);
      setBotSettings({
        enabled: botRes.data.enabled || false,
        system_prompt: botRes.data.system_prompt || "",
        fallback_reply: botRes.data.fallback_reply || "",
        any_message_trigger: botRes.data.any_message_trigger || false,
        trigger_keyword: botRes.data.trigger_keyword || "",
        default_flow_id: botRes.data.default_flow_id || null,
        ai_enabled: botRes.data.ai_enabled || false,
        ai_only_mode: botRes.data.ai_only_mode || false,
        ai_system_prompt: botRes.data.ai_system_prompt || "Eres un asistente amigable y profesional. Responde de forma breve, clara y útil en el mismo idioma del usuario.",
        ai_history_limit: typeof botRes.data.ai_history_limit === "number" ? botRes.data.ai_history_limit : 8,
        ai_in_private: botRes.data.ai_in_private !== false,
        ai_in_groups: botRes.data.ai_in_groups || false,
        ai_model: botRes.data.ai_model || "claude-sonnet-4-5-20250929"
      });
      setGroupSettings({
        enabled: groupRes.data.enabled || false,
        trigger_mode: groupRes.data.trigger_mode || "mention_only",
        trigger_keyword: groupRes.data.trigger_keyword || "",
        trigger_command: groupRes.data.trigger_command || "/bot",
        group_command: groupRes.data.group_command || "",
        builtin_open_admin_only: groupRes.data.builtin_open_admin_only !== false,
        builtin_close_admin_only: groupRes.data.builtin_close_admin_only !== false,
        builtin_kick_admin_only: groupRes.data.builtin_kick_admin_only !== false
      });
      setFlows(flowsRes.data); setCommands(cmdsRes.data); setGroups(groupsRes.data); setMedia(mediaRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- General Tab ---
  const saveGeneral = async () => {
    setSavingGeneral(true); setSavedGeneral(false);
    try {
      await Promise.all([authAxios.put(`${API}/settings/bot`, botSettings), authAxios.put(`${API}/settings/group`, groupSettings)]);
      setSavedGeneral(true); setTimeout(() => setSavedGeneral(false), 3000);
    } catch (err) { console.error(err); } finally { setSavingGeneral(false); }
  };

  // --- Flows ---
  const startNewFlow = () => setEditingFlow({ name: "", trigger_keyword: "", any_message_trigger: false, is_active: true, enable_loop: false, steps: [{ id: `s_${Date.now()}`, step_order: 1, title: "Step 1", response_type: "text", response_content: "", media_url: "", media_asset_id: null, next_step_id: null, is_terminal: false, options: [] }] });
  const editFlow = async (f) => { try { const { data } = await authAxios.get(`${API}/flows/${f.id}`); setEditingFlow(data); } catch { setEditingFlow(f); } };
  const saveFlow = async () => {
    if (!editingFlow.name) return; setSavingFlow(true);
    try { if (editingFlow.id) await authAxios.put(`${API}/flows/${editingFlow.id}`, editingFlow); else await authAxios.post(`${API}/flows`, editingFlow); setEditingFlow(null); const { data } = await authAxios.get(`${API}/flows`); setFlows(data); } catch (err) { console.error(err); } finally { setSavingFlow(false); }
  };
  const deleteFlow = async (id) => { if (!window.confirm("Delete flow?")) return; await authAxios.delete(`${API}/flows/${id}`); const { data } = await authAxios.get(`${API}/flows`); setFlows(data); };
  const addStep = () => { const o = editingFlow.steps.length + 1; setEditingFlow({ ...editingFlow, steps: [...editingFlow.steps, { id: `s_${Date.now()}`, step_order: o, title: `Step ${o}`, response_type: "text", response_content: "", media_url: "", media_asset_id: null, next_step_id: null, is_terminal: false, options: [] }] }); };
  const updateStep = (i, f, v) => { const s = [...editingFlow.steps]; s[i] = { ...s[i], [f]: v }; setEditingFlow({ ...editingFlow, steps: s }); };
  const removeStep = (i) => { setEditingFlow({ ...editingFlow, steps: editingFlow.steps.filter((_, j) => j !== i).map((s, j) => ({ ...s, step_order: j + 1 })) }); };
  const addOption = (i) => { const s = [...editingFlow.steps]; s[i] = { ...s[i], options: [...(s[i].options || []), { id: `o_${Date.now()}`, label: "", value: "", next_step_id: null }] }; setEditingFlow({ ...editingFlow, steps: s }); };
  const updateOption = (si, oi, f, v) => { const s = [...editingFlow.steps]; const opts = [...(s[si].options || [])]; opts[oi] = { ...opts[oi], [f]: v }; s[si] = { ...s[si], options: opts }; setEditingFlow({ ...editingFlow, steps: s }); };
  const removeOption = (si, oi) => { const s = [...editingFlow.steps]; s[si] = { ...s[si], options: s[si].options.filter((_, j) => j !== oi) }; setEditingFlow({ ...editingFlow, steps: s }); };

  // Upload media for a flow step
  const uploadStepMedia = async (stepIdx, file) => {
    if (!file) return;
    setUploadingStepMedia(stepIdx);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await authAxios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      updateStep(stepIdx, "media_url", data.url);
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingStepMedia(null); }
  };

  // --- Group Commands ---
  const startNewCmd = () => setEditingCmd({ command: ".", response_type: "text", response_content: "", media_url: "", media_asset_id: null, is_active: true, group_chat_id: null, admin_only: false });
  const saveCmd = async () => { if (!editingCmd.command?.startsWith(".")) return; setSavingCmd(true); try { if (editingCmd.id) await authAxios.put(`${API}/group-commands/${editingCmd.id}`, editingCmd); else await authAxios.post(`${API}/group-commands`, editingCmd); setEditingCmd(null); const { data } = await authAxios.get(`${API}/group-commands`); setCommands(data); } catch (err) { alert(err.response?.data?.detail || "Error"); } finally { setSavingCmd(false); } };
  const deleteCmd = async (id) => { if (!window.confirm("Delete?")) return; await authAxios.delete(`${API}/group-commands/${id}`); const { data } = await authAxios.get(`${API}/group-commands`); setCommands(data); };

  // Upload media for group command
  const uploadCmdMedia = async (file) => {
    if (!file) return;
    setUploadingCmdMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await authAxios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingCmd(prev => ({ ...prev, media_url: data.url }));
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingCmdMedia(false); }
  };

  // --- Groups ---
  const syncGroups = async () => {
    setSyncing(true);
    try {
      // Check WA status first
      const { data: waStatus } = await authAxios.get(`${API}/whatsapp/status`).catch(() => ({ data: { status: 'disconnected' } }));
      if (waStatus.status !== 'connected') {
        setExportWarning("WhatsApp is not connected. Please connect your session first.");
        setSyncing(false);
        return;
      }
      await authAxios.post(`${API}/groups/sync`);
      const { data } = await authAxios.get(`${API}/groups`);
      setGroups(data);
      setExportWarning(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('NOT_CONNECTED')) {
        setExportWarning("WhatsApp is not connected. Please connect your session first.");
      } else {
        setExportWarning(detail || "Failed to sync groups. Check your WhatsApp connection.");
      }
    } finally { setSyncing(false); }
  };
  const toggleGroup = async (g, v) => { try { await authAxios.put(`${API}/groups/${g.id}/toggle`, { is_enabled: v }); setGroups(groups.map(x => x.id === g.id ? { ...x, is_enabled: v } : x)); } catch (err) { console.error(err); } };

  const openAdminInstructions = async (g) => {
    setAdminInstructions({ group: g, data: null });
    setAdminInstructionsLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/groups/${encodeURIComponent(g.group_chat_id)}/admin-status`);
      setAdminInstructions({ group: g, data });
      // Refresh local state so badge updates if bot is now admin
      if (data && data.bot_is_admin) {
        setGroups(prev => prev.map(x => x.id === g.id ? { ...x, bot_is_admin: true } : x));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setAdminInstructions({ group: g, data: null, error: detail === "WA_NOT_CONNECTED" ? "WhatsApp is not connected. Please connect first." : "Could not check admin status. Make sure WhatsApp is connected." });
    } finally { setAdminInstructionsLoading(false); }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopiedPhone(true); setTimeout(() => setCopiedPhone(false), 1500); } catch { /* ignore */ }
  };

  const _parseExportError = async (err) => {
    const errMessages = {
      NOT_CONNECTED: "WhatsApp is not connected. Please connect your session first.",
      NO_GROUPS_SELECTED: "No groups are selected. Enable at least one group above.",
      NO_PARTICIPANTS_FOUND: "No participants found in this group.",
      GROUP_NOT_FOUND: "Group not found.",
      EXCEL_GENERATION_FAILED: "Failed to generate Excel file. Please try again.",
      FILE_RESPONSE_FAILED: "Failed to create file download. Please try again.",
      EXPORT_FAILED: "Export failed. Please try again.",
      EXPORT_NOT_ENABLED: "🔒 La exportación de contactos es un módulo premium. Pídele al super admin que te lo active.",
    };
    try {
      // Case 1: Blob error response (from axios with responseType: 'blob')
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const d = JSON.parse(text);
          if (d.detail && errMessages[d.detail]) return errMessages[d.detail];
          if (d.detail) return String(d.detail);
        } catch { /* blob parse failed */ }
      }
      // Case 2: Plain object with detail (our own thrown errors)
      if (err?.response?.data?.detail) {
        const code = err.response.data.detail;
        return errMessages[code] || String(code);
      }
      // Case 3: Direct detail string
      if (err?.detail) return errMessages[err.detail] || String(err.detail);
      // Case 4: Standard error message
      if (err?.message) return String(err.message);
    } catch { /* safety net */ }
    return errMessages.EXPORT_FAILED;
  };

  const _downloadBlob = async (resp, filename) => {
    // Check if response is actually a JSON error (content-type includes json)
    try {
      if (resp.data && typeof resp.data.type === 'string' && resp.data.type.includes('json')) {
        const text = await resp.data.text();
        const d = JSON.parse(text);
        const code = d.detail || 'EXPORT_FAILED';
        const errMessages = {
          NOT_CONNECTED: "WhatsApp is not connected. Please connect your session first.",
          NO_GROUPS_SELECTED: "No groups are selected. Enable at least one group above.",
          NO_PARTICIPANTS_FOUND: "No participants found in this group.",
          GROUP_NOT_FOUND: "Group not found.",
          EXCEL_GENERATION_FAILED: "Failed to generate Excel file. Please try again.",
          EXPORT_FAILED: "Export failed. Please try again.",
        };
        throw new Error(errMessages[code] || code);
      }
    } catch (e) {
      if (e instanceof Error) throw e;
      throw new Error("Export failed. Please try again.");
    }
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); window.URL.revokeObjectURL(url);
  };

  const exportContacts = async () => {
    setExporting(true);
    setExportWarning(null);
    try {
      let check = {};
      try {
        const resp = await authAxios.get(`${API}/groups/export-contacts/check`);
        check = resp.data || {};
      } catch { check = {}; }

      if (check.wa_connected === false || check.error === 'NOT_CONNECTED') { setExportWarning("WhatsApp is not connected. Please connect your session first."); return; }
      if (check.groups_count === 0) { setExportWarning("No groups are selected. Enable at least one group above."); return; }
      if (check.count === 0) { setExportWarning("No participants found in the selected groups."); return; }

      const resp = await authAxios.get(`${API}/groups/export-contacts`, { responseType: 'blob', timeout: 600000 });
      await _downloadBlob(resp, 'group_contacts.xlsx');
    } catch (err) {
      let msg = "Export failed. Please try again.";
      try {
        msg = (err instanceof Error && err.message) ? err.message : (await _parseExportError(err)) || msg;
      } catch { /* keep default */ }
      setExportWarning(msg);
    } finally { setExporting(false); }
  };

  const exportSingleGroup = async (g) => {
    setExportingSingle(g.group_chat_id);
    setExportWarning(null);
    try {
      const resp = await authAxios.get(`${API}/groups/export-contacts/single/${encodeURIComponent(g.group_chat_id)}`, { responseType: 'blob', timeout: 600000 });
      const safeName = (g.group_name || 'group').replace(/[^a-zA-Z0-9 _-]/g, '').substring(0, 30);
      await _downloadBlob(resp, `${safeName}_contacts.xlsx`);
    } catch (err) {
      let msg = "Export failed. Please try again.";
      try {
        msg = (err instanceof Error && err.message) ? err.message : (await _parseExportError(err)) || msg;
      } catch { /* keep default */ }
      setExportWarning(msg);
    } finally { setExportingSingle(null); }
  };

  // --- Media Library ---
  const handleMediaUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingMedia(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", uploadMediaName.trim() || file.name);
        await authAxios.post(`${API}/upload/media-library`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setUploadMediaName("");
      const { data } = await authAxios.get(`${API}/media`); setMedia(data);
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingMedia(false); }
  };

  const handleMediaDrop = (e) => { e.preventDefault(); setDragOverMedia(false); handleMediaUpload(e.dataTransfer.files); };
  const deleteMedia = async (id) => { await authAxios.delete(`${API}/media/${id}`); setMedia(media.filter(m => m.id !== id)); };

  if (loading) return <div className="p-8"><div className="animate-pulse h-96 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8" data-testid="bot-studio-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2"><Bot className="w-6 h-6" /> Bot Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure your bot, flows, group commands, and media in one place</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general"><Bot className="w-3.5 h-3.5 mr-1.5" />General</TabsTrigger>
          {canFlows && <TabsTrigger value="flows" data-testid="tab-flows"><GitBranch className="w-3.5 h-3.5 mr-1.5" />Flows</TabsTrigger>}
          <TabsTrigger value="group-commands" data-testid="tab-group-commands"><Terminal className="w-3.5 h-3.5 mr-1.5" />Group Commands</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups"><Users className="w-3.5 h-3.5 mr-1.5" />Groups Sync</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media"><Image className="w-3.5 h-3.5 mr-1.5" />Media Library</TabsTrigger>
        </TabsList>

        {/* ===== GENERAL TAB ===== */}
        <TabsContent value="general">
          <div className="space-y-6 max-w-2xl">
            {canPrivateBot && (
            <Card>
              <CardHeader><div className="flex items-center justify-between"><div><CardTitle className="text-base">Private Chat Bot</CardTitle><CardDescription>Auto-reply settings for private messages</CardDescription></div><Switch checked={botSettings.enabled} onCheckedChange={v => setBotSettings({...botSettings, enabled: v})} className="data-[state=checked]:bg-[#22c55e]" data-testid="bot-enabled-toggle" /></div></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-3 rounded-lg border bg-slate-50">
                  <Switch checked={botSettings.any_message_trigger} onCheckedChange={v => setBotSettings({...botSettings, any_message_trigger: v})} data-testid="any-message-toggle" />
                  <div><Label className="text-sm font-medium">Any message trigger</Label><p className="text-xs text-muted-foreground">When ON, any incoming message starts a flow</p></div>
                </div>
                {!botSettings.any_message_trigger && (
                  <div className="space-y-2"><Label>Trigger Keyword</Label><Input value={botSettings.trigger_keyword} onChange={e => setBotSettings({...botSettings, trigger_keyword: e.target.value})} placeholder="hello" data-testid="bot-trigger-keyword" /><p className="text-xs text-muted-foreground">Only this keyword starts a flow</p></div>
                )}
                {/* Default Flow Selector */}
                <div className="space-y-2">
                  <Label>Default Flow</Label>
                  <select
                    value={botSettings.default_flow_id || ""}
                    onChange={e => setBotSettings({...botSettings, default_flow_id: e.target.value || null})}
                    className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="default-flow-selector"
                  >
                    <option value="">No default flow (use fallback reply)</option>
                    {flows.filter(f => f.is_active).map(f => (
                      <option key={f.id} value={f.id}>{f.name} ({f.trigger_keyword || "any"})</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">When a matching flow is not found, this default flow will run instead of the fallback reply</p>
                </div>
                <div className="space-y-2"><Label>System Prompt</Label><Textarea value={botSettings.system_prompt} onChange={e => setBotSettings({...botSettings, system_prompt: e.target.value})} rows={3} placeholder="AI prompt (future use)" data-testid="system-prompt" /></div>
                <div className="space-y-2"><Label>Fallback Reply</Label><Textarea value={botSettings.fallback_reply} onChange={e => setBotSettings({...botSettings, fallback_reply: e.target.value})} rows={2} placeholder="Default reply when no flow matches" data-testid="fallback-reply" /></div>
              </CardContent>
            </Card>
            )}

            {/* AI Smart Replies */}
            {canAI && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50/40 to-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Respuestas con IA</CardTitle>
                    <CardDescription>Respuesta inteligente cuando ningún flujo o comando coincide. Powered by Claude Sonnet 4.5.</CardDescription>
                  </div>
                  <Switch checked={botSettings.ai_enabled} onCheckedChange={v => setBotSettings({...botSettings, ai_enabled: v})} className="data-[state=checked]:bg-purple-600" data-testid="ai-enabled-toggle" />
                </div>
              </CardHeader>
              {botSettings.ai_enabled && (
                <CardContent className="space-y-4">
                  {/* AI-only mode toggle (highlighted) */}
                  <div className={`flex items-start gap-4 p-4 rounded-xl border-2 transition ${botSettings.ai_only_mode ? "border-purple-500 bg-purple-50" : "border-purple-200 bg-white"}`}>
                    <Switch
                      checked={botSettings.ai_only_mode}
                      onCheckedChange={v => setBotSettings({...botSettings, ai_only_mode: v})}
                      className="data-[state=checked]:bg-purple-600 mt-0.5"
                      data-testid="ai-only-mode-toggle"
                    />
                    <div className="flex-1">
                      <Label className="text-sm font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" /> Solo IA (modo asistente puro)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Cuando está <strong>activado</strong>: la IA responde TODOS los mensajes según su prompt — se omiten comandos (.precio, .menu), palabras clave y flujos.
                        <br />Cuando está <strong>desactivado</strong>: la IA solo responde cuando ningún flujo, comando o palabra clave matchea (modo fallback inteligente, recomendado para tiendas).
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center justify-between">
                      <span>Prompt de sistema (instrucciones para la IA)</span>
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setTemplatesOpen(true)} data-testid="open-templates-btn" className="h-7 px-2 text-xs">
                          <Sparkles className="w-3 h-3 mr-1 text-amber-500" /> Plantillas listas
                        </Button>
                        <span className="text-xs text-muted-foreground font-normal">{(botSettings.ai_system_prompt || "").length} caracteres</span>
                      </div>
                    </Label>
                    <Textarea
                      value={botSettings.ai_system_prompt}
                      onChange={e => setBotSettings({...botSettings, ai_system_prompt: e.target.value})}
                      rows={8}
                      placeholder={`Ejemplo:\n\nEres el asistente de "Tienda Bo-Tito".\n\n📋 Reglas:\n• Saludá con calidez y respondé en el idioma del usuario.\n• Cuando pregunten por precios, ofrecé mandar el catálogo.\n• Si piden hablar con humano, decí que un asesor responde en el horario 9-18.\n• Nunca inventes datos: si no sabés algo, decí que vas a consultar.\n\n💼 Datos del negocio:\n• Horario: Lun-Vie 9 a 18 hs\n• WhatsApp: +54 11 5555-5555\n• Envíos: a todo el país en 24-48 hs`}
                      className="font-mono text-xs leading-relaxed min-h-[180px]"
                      data-testid="ai-system-prompt"
                    />
                    <p className="text-xs text-muted-foreground">
                      Acá definís la <strong>personalidad, reglas y datos</strong> de la IA. Cuanto más específico, mejor responde.
                      Tip: usá viñetas para reglas y listas para datos del negocio.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Modelo</Label>
                      <select
                        value={botSettings.ai_model}
                        onChange={e => setBotSettings({...botSettings, ai_model: e.target.value})}
                        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                        data-testid="ai-model-selector"
                      >
                        <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (recomendado)</option>
                        <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (más rápido y barato)</option>
                        <option value="claude-opus-4-5-20251101">Claude Opus 4.5 (más potente)</option>
                        <option value="gpt-5.1">OpenAI GPT-5.1</option>
                        <option value="gpt-4o-mini">OpenAI GPT-4o-mini</option>
                        <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Memoria de mensajes</Label>
                      <Input
                        type="number"
                        min={0}
                        max={30}
                        value={botSettings.ai_history_limit}
                        onChange={e => setBotSettings({...botSettings, ai_history_limit: Math.max(0, Math.min(30, parseInt(e.target.value) || 0))})}
                        data-testid="ai-history-limit"
                      />
                      <p className="text-xs text-muted-foreground">Últimos N mensajes (0–30)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 rounded-lg border bg-white">
                    <Switch checked={botSettings.ai_in_private} onCheckedChange={v => setBotSettings({...botSettings, ai_in_private: v})} className="data-[state=checked]:bg-purple-600" data-testid="ai-in-private-toggle" />
                    <div><Label className="text-sm font-medium">Activar en chats privados</Label><p className="text-xs text-muted-foreground">Cuando ningún flujo matchea, responde la IA</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-3 rounded-lg border bg-white">
                    <Switch checked={botSettings.ai_in_groups} onCheckedChange={v => setBotSettings({...botSettings, ai_in_groups: v})} className="data-[state=checked]:bg-purple-600" data-testid="ai-in-groups-toggle" />
                    <div><Label className="text-sm font-medium">Activar en grupos</Label><p className="text-xs text-muted-foreground">Solo después del trigger del grupo (mención/keyword/all)</p></div>
                  </div>
                  <div className={`text-xs rounded-lg p-3 border ${botSettings.ai_only_mode ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-purple-50 border-purple-200 text-muted-foreground"}`}>
                    {botSettings.ai_only_mode
                      ? <>⚠️ <b>Modo Solo IA activado</b> — los comandos (.precio, .menu), palabras clave y flujos están <b>desactivados</b>. La IA responde todo según su prompt.</>
                      : <>💡 La IA solo responde como <b>fallback inteligente</b> — los flujos, comandos y reglas siempre tienen prioridad.</>}
                  </div>
                </CardContent>
              )}
            </Card>
            )}
            <Card>
              <CardHeader><div className="flex items-center justify-between"><div><CardTitle className="text-base">Group Chat Bot</CardTitle><CardDescription>Enable bot responses in groups</CardDescription></div><Switch checked={groupSettings.enabled} onCheckedChange={v => setGroupSettings({...groupSettings, enabled: v})} className="data-[state=checked]:bg-[#22c55e]" data-testid="group-enabled-toggle" /></div></CardHeader>
              <CardContent className="space-y-3">
                <Label>Trigger Mode</Label>
                {[
                  {v:"mention_only",l:"Mention Only",d:"Bot replies when mentioned with @"},
                  {v:"keyword",l:"Keyword",d:"Bot replies when a keyword is detected"},
                  {v:"slash_command",l:"Slash Command",d:"Bot replies to /command style triggers"},
                  {v:"group_command",l:"Group Command",d:"Bot replies to a specific command text"}
                ].map(m => (
                  <button key={m.v} onClick={() => setGroupSettings({...groupSettings, trigger_mode: m.v})} className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${groupSettings.trigger_mode === m.v ? "border-[#0f172a] bg-slate-50" : "border-slate-200"}`} data-testid={`trigger-mode-${m.v}`}>
                    <div className="flex items-center gap-2"><span className="text-sm font-medium">{m.l}</span>{groupSettings.trigger_mode === m.v && <Badge className="bg-[#0f172a] text-white text-xs">Active</Badge>}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.d}</p>
                  </button>
                ))}
                {groupSettings.trigger_mode === "keyword" && <div className="space-y-2"><Label>Keyword</Label><Input value={groupSettings.trigger_keyword} onChange={e => setGroupSettings({...groupSettings, trigger_keyword: e.target.value})} data-testid="group-keyword" /></div>}
                {groupSettings.trigger_mode === "slash_command" && <div className="space-y-2"><Label>Command</Label><Input value={groupSettings.trigger_command} onChange={e => setGroupSettings({...groupSettings, trigger_command: e.target.value})} data-testid="group-command-input" /></div>}
                {groupSettings.trigger_mode === "group_command" && <div className="space-y-2"><Label>Group Command</Label><Input value={groupSettings.group_command} onChange={e => setGroupSettings({...groupSettings, group_command: e.target.value})} placeholder=".menu" data-testid="group-command-text" /><p className="text-xs text-muted-foreground">Exact text match (e.g. .menu or any custom text)</p></div>}
              </CardContent>
            </Card>
            <div className="flex items-center gap-3">
              <Button onClick={saveGeneral} disabled={savingGeneral} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="save-general">{savingGeneral ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{savingGeneral ? "Saving..." : "Save Settings"}</Button>
              {savedGeneral && <span className="text-sm text-[#22c55e] font-medium">Saved</span>}
            </div>
          </div>
        </TabsContent>

        {/* ===== FLOWS TAB ===== */}
        {canFlows && (
        <TabsContent value="flows">
          <FlowsTabContent flows={flows} onRefresh={async () => { const { data } = await authAxios.get(`${API}/flows`); setFlows(data); }} onDelete={async (id) => { if (!window.confirm("Delete flow?")) return; await authAxios.delete(`${API}/flows/${id}`); const { data } = await authAxios.get(`${API}/flows`); setFlows(data); }} />
        </TabsContent>
        )}

        {/* ===== GROUP COMMANDS TAB ===== */}
        <TabsContent value="group-commands">
          <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-medium">Group Commands ({commands.length})</h2><Button onClick={startNewCmd} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="new-cmd-btn"><Plus className="w-4 h-4 mr-1" /> New Command</Button></div>

          {/* Built-in admin commands info */}
          <Card className="mb-4 border-amber-200 bg-amber-50/50" data-testid="builtin-cmds-card">
            <CardContent className="p-4">
              <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1"><Terminal className="w-4 h-4" /> Built-in Group Commands</div>
              <p className="text-xs text-amber-800/80 mb-3">Always available in any enabled group. The bot must be a group admin to execute them. Toggle each one between <b>Admin only</b> and <b>Everyone</b>.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                {[
                  { cmd: ".open", label: "Opens the group (everyone can write)", key: "builtin_open_admin_only" },
                  { cmd: ".close", label: "Closes the group (admins only)", key: "builtin_close_admin_only" },
                  { cmd: ".kick", label: "Removes the user of the previous message (or the quoted one)", key: "builtin_kick_admin_only" }
                ].map(b => (
                  <div key={b.cmd} className="p-3 bg-white rounded border border-amber-200 flex flex-col gap-2" data-testid={`builtin-${b.cmd.slice(1)}`}>
                    <div><code className="font-bold">{b.cmd}</code></div>
                    <p className="text-muted-foreground leading-snug">{b.label}</p>
                    <div className="flex items-center justify-between pt-1 border-t border-amber-100">
                      <span className={groupSettings[b.key] ? "text-amber-700 font-medium" : "text-emerald-700 font-medium"}>{groupSettings[b.key] ? "Admin only" : "Everyone"}</span>
                      <Switch
                        checked={!!groupSettings[b.key]}
                        onCheckedChange={async v => {
                          const next = { ...groupSettings, [b.key]: v };
                          setGroupSettings(next);
                          try { await authAxios.put(`${API}/settings/group`, next); } catch (e) { console.error(e); }
                        }}
                        className="data-[state=checked]:bg-amber-500"
                        data-testid={`builtin-toggle-${b.cmd.slice(1)}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {commands.length === 0 ? <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">No commands yet. Create dot-commands like .menu .help</CardContent></Card> : (
            <div className="space-y-2">{commands.map(c => (
              <Card key={c.id} data-testid={`cmd-${c.id}`}><CardContent className="p-4 flex items-center justify-between">
                <div><code className="text-sm font-bold bg-slate-100 px-2 py-0.5 rounded">{c.command}</code><Badge className={`ml-2 ${c.is_active ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}`}>{c.is_active ? "Active" : "Off"}</Badge><Badge variant="outline" className="ml-1 text-[10px]">{c.response_type}</Badge>{c.admin_only && <Badge className="ml-1 text-[10px] bg-amber-500 text-white">Admin only</Badge>}<p className="text-xs text-muted-foreground mt-1 truncate max-w-md">{c.response_content || c.media_url || "No content"}</p></div>
                <div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => setEditingCmd({...c})} data-testid={`edit-cmd-${c.id}`}>Edit</Button><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteCmd(c.id)}><Trash2 className="w-4 h-4" /></Button></div>
              </CardContent></Card>
            ))}</div>
          )}
          <Dialog open={!!editingCmd} onOpenChange={o => !o && setEditingCmd(null)}>
            <DialogContent data-testid="cmd-editor"><DialogHeader><DialogTitle>{editingCmd?.id ? "Edit Command" : "New Command"}</DialogTitle></DialogHeader>
              {editingCmd && <div className="space-y-4">
                <div className="space-y-2"><Label>Command (starts with .)</Label><Input value={editingCmd.command} onChange={e => setEditingCmd({...editingCmd, command: e.target.value})} placeholder=".menu" data-testid="cmd-input" /><p className="text-xs text-muted-foreground">Example: .menu .help .price .info</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Response Type</Label><select value={editingCmd.response_type} onChange={e => setEditingCmd({...editingCmd, response_type: e.target.value})} className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"><option value="text">Text</option><option value="image">Image</option><option value="audio">Audio</option><option value="video">Video</option><option value="document">Document</option></select></div>
                  <div className="space-y-2"><Label>Scope</Label><select value={editingCmd.group_chat_id || ""} onChange={e => setEditingCmd({...editingCmd, group_chat_id: e.target.value || null})} className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"><option value="">All enabled groups</option>{groups.map(g => <option key={g.group_chat_id} value={g.group_chat_id}>{g.group_name}</option>)}</select></div>
                </div>
                {editingCmd.response_type === "text" ? (
                  <div className="space-y-2"><Label>Response Text</Label><Textarea value={editingCmd.response_content} onChange={e => setEditingCmd({...editingCmd, response_content: e.target.value})} rows={4} data-testid="cmd-content" /></div>
                ) : (
                  <div className="space-y-2">
                    <Label>Media</Label>
                    <div className="flex gap-2">
                      <Input value={editingCmd.media_url} onChange={e => setEditingCmd({...editingCmd, media_url: e.target.value})} placeholder="Media URL or upload..." className="flex-1" />
                      <Button size="sm" variant="outline" onClick={() => cmdFileRef.current?.click()} disabled={uploadingCmdMedia} data-testid="upload-cmd-media">
                        {uploadingCmdMedia ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      </Button>
                      <input ref={cmdFileRef} type="file" className="hidden" accept="image/*,audio/*,video/*,.pdf,.doc,.docx" onChange={e => e.target.files?.[0] && uploadCmdMedia(e.target.files[0])} />
                    </div>
                    <Textarea value={editingCmd.response_content} onChange={e => setEditingCmd({...editingCmd, response_content: e.target.value})} rows={2} placeholder="Caption (optional)" className="text-sm" />
                    {media.length > 0 && <select value={editingCmd.media_asset_id || ""} onChange={e => { const m = media.find(x=>x.id===e.target.value); if(m) setEditingCmd({...editingCmd, media_url: m.file_url, media_asset_id: m.id}); }} className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"><option value="">Select from Media Library</option>{media.filter(m=>m.file_type===editingCmd.response_type).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select>}
                  </div>
                )}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2"><Switch checked={editingCmd.is_active} onCheckedChange={v => setEditingCmd({...editingCmd, is_active: v})} className="data-[state=checked]:bg-[#22c55e]" data-testid="cmd-active-switch" /><Label>Active</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={!!editingCmd.admin_only} onCheckedChange={v => setEditingCmd({...editingCmd, admin_only: v})} className="data-[state=checked]:bg-amber-500" data-testid="cmd-admin-only-switch" /><Label>Admin only <span className="text-xs text-muted-foreground">(only group admins can use)</span></Label></div>
                </div>
              </div>}
              <DialogFooter><Button variant="outline" onClick={() => setEditingCmd(null)}>Cancel</Button><Button onClick={saveCmd} disabled={savingCmd} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="save-cmd">{savingCmd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== GROUPS SYNC TAB ===== */}
        <TabsContent value="groups">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Groups ({groups.length})</h2>
            <div className="flex gap-2">
              {canExport && (
                <Button onClick={exportContacts} disabled={exporting || groups.filter(g=>g.is_enabled).length === 0} variant="outline" data-testid="export-contacts-btn">{exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}{exporting ? "Exporting..." : "Export All"}</Button>
              )}
              <Button onClick={syncGroups} disabled={syncing} variant="outline" data-testid="sync-groups-btn">{syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sync Groups</Button>
            </div>
          </div>
          {/* Export Warning */}
          {exportWarning && (
            <Card className="mb-4 border-amber-300 bg-amber-50">
              <CardContent className="p-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">{exportWarning}</p>
              </CardContent>
            </Card>
          )}
          {/* Search bar */}
          {groups.length > 0 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Search groups by name..." className="pl-9" data-testid="group-search-input" />
            </div>
          )}
          {groups.length === 0 ? <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">No groups. Connect WhatsApp and click Sync.</CardContent></Card> : (
            <Card><CardContent className="p-0">{groups.filter(g => !groupSearch || g.group_name?.toLowerCase().includes(groupSearch.toLowerCase())).map(g => (
              <div key={g.id} className="flex items-center justify-between px-6 py-3 border-b last:border-0 hover:bg-slate-50" data-testid={`group-${g.id}`}>
                <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><Users className="w-4 h-4 text-slate-500" /></div><div><p className="text-sm font-medium">{g.group_name}</p><p className="text-xs text-muted-foreground truncate max-w-xs">{g.group_chat_id}</p></div></div>
                <div className="flex items-center gap-3">
                  {g.bot_is_admin
                    ? <Badge className="bg-emerald-500 text-white" data-testid={`bot-admin-${g.id}`}>Bot is admin</Badge>
                    : <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-700 hover:bg-amber-50" onClick={() => openAdminInstructions(g)} data-testid={`make-admin-${g.id}`}><ShieldAlert className="w-3 h-3 mr-1" />Make bot admin</Button>}
                  {canExport && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => exportSingleGroup(g)} disabled={!!exportingSingle} data-testid={`export-group-${g.id}`}>
                      {exportingSingle === g.group_chat_id ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Exporting...</> : <><Download className="w-3 h-3 mr-1" />Export</>}
                    </Button>
                  )}
                  <Badge className={g.is_enabled ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>{g.is_enabled ? "Enabled" : "Disabled"}</Badge>
                  <Switch checked={g.is_enabled} onCheckedChange={v => toggleGroup(g, v)} className="data-[state=checked]:bg-[#22c55e]" data-testid={`toggle-group-${g.id}`} />
                </div>
              </div>
            ))}</CardContent></Card>
          )}

          {/* Bot Admin Instructions Dialog */}
          <Dialog open={!!adminInstructions} onOpenChange={o => !o && setAdminInstructions(null)}>
            <DialogContent data-testid="admin-instructions-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-500" />Make the bot a group admin</DialogTitle>
              </DialogHeader>
              {adminInstructionsLoading ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Checking status...</div>
              ) : adminInstructions?.error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="admin-instructions-error">{adminInstructions.error}</div>
              ) : adminInstructions?.data?.bot_is_admin ? (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800">✅ The bot is already an admin in <b>{adminInstructions?.data?.group_name || adminInstructions?.group?.group_name}</b>. All built-in commands will work.</div>
              ) : adminInstructions?.data ? (
                <div className="space-y-4 text-sm">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="font-medium text-amber-900 mb-1">The bot is <span className="underline">not</span> an admin in this group yet.</p>
                    <p className="text-amber-800 text-xs">WhatsApp does not allow apps to self-promote. An existing group admin must promote the bot's number manually.</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Bot's WhatsApp number:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-slate-100 rounded text-sm font-mono">+{adminInstructions.data.bot_phone || ""}</code>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard("+" + (adminInstructions.data.bot_phone || ""))} data-testid="copy-bot-phone">
                        {copiedPhone ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Steps for the group admin:</p>
                    <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground">
                      <li>Open WhatsApp and go to the group <b>{adminInstructions?.data?.group_name || adminInstructions?.group?.group_name}</b></li>
                      <li>Tap the group name at the top to open Group Info</li>
                      <li>Scroll to the participants list and tap the bot's number above</li>
                      <li>Select <b>"Make group admin"</b></li>
                      <li>Come back here and click <b>"Recheck"</b> to verify</li>
                    </ol>
                  </div>
                  {(adminInstructions.data.admin_phones?.length > 0 || adminInstructions.data.admin_unknown_count > 0) && (
                    <div className="space-y-2">
                      <p className="font-medium">Current admins of this group ({(adminInstructions.data.admin_phones?.length || 0) + (adminInstructions.data.admin_unknown_count || 0)}):</p>
                      <div className="flex flex-wrap gap-1 text-xs">
                        {(adminInstructions.data.admin_phones || []).slice(0, 12).map((p, i) => (
                          <code key={i} className="px-2 py-0.5 bg-slate-100 rounded">+{p}</code>
                        ))}
                        {adminInstructions.data.admin_phones?.length > 12 && <span className="text-muted-foreground">+{adminInstructions.data.admin_phones.length - 12} más</span>}
                        {adminInstructions.data.admin_unknown_count > 0 && (
                          <span className="text-muted-foreground italic">
                            {(adminInstructions.data.admin_phones?.length || 0) > 0 ? "y " : ""}{adminInstructions.data.admin_unknown_count} admin{adminInstructions.data.admin_unknown_count > 1 ? "s" : ""} con número oculto por privacidad de WhatsApp
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
              <DialogFooter>
                <Button variant="outline" onClick={() => setAdminInstructions(null)}>Close</Button>
                {adminInstructions?.group && !adminInstructions?.data?.bot_is_admin && (
                  <Button onClick={() => openAdminInstructions(adminInstructions.group)} disabled={adminInstructionsLoading} data-testid="recheck-admin-btn">
                    {adminInstructionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Recheck
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== MEDIA LIBRARY TAB ===== */}
        <TabsContent value="media">
          <div className="mb-4"><h2 className="text-lg font-medium">Media Library ({media.length})</h2><p className="text-sm text-muted-foreground">Upload files to Cloudinary for reuse in flows and commands</p></div>
          <Card className="mb-4">
            <CardContent className="p-4">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragOverMedia ? "border-[#22c55e] bg-green-50" : "border-slate-300 hover:border-slate-400"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverMedia(true); }}
                onDragLeave={() => setDragOverMedia(false)}
                onDrop={handleMediaDrop}
                onClick={() => mediaFileRef.current?.click()}
                data-testid="media-dropzone"
              >
                {uploadingMedia ? (
                  <div className="flex flex-col items-center gap-2"><Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" /><p className="text-sm text-muted-foreground">Uploading to Cloudinary...</p></div>
                ) : (
                  <div className="flex flex-col items-center gap-2"><Upload className="w-8 h-8 text-slate-400" /><p className="text-sm font-medium">Drag & drop or click to upload</p><p className="text-xs text-muted-foreground">Images, audio, video, documents (max 50MB)</p></div>
                )}
              </div>
              <input ref={mediaFileRef} type="file" multiple className="hidden" accept="image/*,audio/*,video/*,.pdf,.doc,.docx" onChange={e => handleMediaUpload(e.target.files)} />
              <div className="flex gap-2 mt-3">
                <Input value={uploadMediaName} onChange={e => setUploadMediaName(e.target.value)} placeholder="Custom name (optional)" className="text-sm flex-1" data-testid="media-upload-name" />
              </div>
            </CardContent>
          </Card>
          {media.length === 0 ? <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">No media assets yet</CardContent></Card> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">{media.map(m => (
              <Card key={m.id} data-testid={`media-${m.id}`}><CardContent className="p-3">
                {m.file_type === "image" && m.file_url ? <img src={m.file_url} alt={m.name} className="w-full h-28 object-cover rounded mb-2 bg-slate-100" /> : <div className="w-full h-28 rounded mb-2 bg-slate-100 flex items-center justify-center text-xs text-slate-400 uppercase">{m.file_type}</div>}
                <p className="text-sm font-medium truncate">{m.name}</p>
                <div className="flex items-center justify-between mt-1"><Badge variant="outline" className="text-[10px]">{m.file_type}</Badge><Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteMedia(m.id)} data-testid={`delete-media-${m.id}`}><Trash2 className="w-3 h-3" /></Button></div>
              </CardContent></Card>
            ))}</div>
          )}
        </TabsContent>
      </Tabs>

      <TemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        onPromptLoaded={(prompt) => setBotSettings((prev) => ({ ...prev, ai_system_prompt: prompt, ai_enabled: true }))}
      />
    </div>
  );
}


function FlowsTabContent({ flows, onRefresh, onDelete }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Reply Flows ({flows.length})</h2>
        <Button onClick={() => navigate('/dashboard/flow-builder')} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="new-flow-btn">
          <Plus className="w-4 h-4 mr-1" /> New Visual Flow
        </Button>
      </div>
      {flows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <GitBranch className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-muted-foreground mb-4">No flows yet. Create your first visual flow.</p>
            <Button onClick={() => navigate('/dashboard/flow-builder')} className="bg-[#22c55e] hover:bg-[#16a34a] text-white">
              <Plus className="w-4 h-4 mr-1" /> Create Flow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {flows.map(f => (
            <Card key={f.id} data-testid={`flow-${f.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0f172a] flex items-center justify-center">
                    <GitBranch className="w-4 h-4 text-[#22c55e]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{f.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.any_message_trigger ? "Any message" : `Trigger: ${f.trigger_keyword || '—'}`}
                      {' '}&middot;{' '}{f.nodes ? `${f.nodes.length} nodes` : `${(f.steps || []).length} steps`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={f.is_active ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>{f.is_active ? "Active" : "Off"}</Badge>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/dashboard/flow-builder/${f.id}`)} data-testid={`edit-flow-${f.id}`}>
                    Open Builder
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(f.id)} data-testid={`delete-flow-${f.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
